import React, { useEffect, useState, useCallback } from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import { useAuth } from "../context/AuthContext";
import "./VintedInboxPage.css";

const VintedInboxPage = () => {
  const { user, companyId } = useAuth();
// eslint-disable-next-line
  const userId = user?.id || user?.user_id || user?.userId;

  const [conversations, setConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  // 🆕 Sync userId with extension on page load
  useEffect(() => {
    if (!companyId) return;
    fetch(
      "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId }),
      }
    ).catch((err) => console.warn("UserId sync failed", err));
  }, [companyId]);

  const fetchConversations = useCallback(async () => {
    if (!companyId) return;
    setLoadingConversations(true);
    try {
      const res = await fetch(
        `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/inbox/conversations?userId=${companyId}`
      );
      const data = await res.json();
      if (data.success) {
        setConversations(data.conversations);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingConversations(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const fetchMessages = async (conversationId) => {
    setSelectedConvId(conversationId);
    setLoadingMessages(true);
    try {
      const res = await fetch(
        `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/inbox/conversation/${conversationId}?userId=${companyId}`
      );
      const data = await res.json();
      if (data.success) {
        setMessages(data.messages);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendToVendor = async () => {
    if (!selectedConvId) {
      alert("Please select a conversation first.");
      return;
    }
    const vendorPhone = prompt(
      "Enter vendor WhatsApp number with country code (e.g., 49123456789):"
    );
    if (!vendorPhone) return;

    setSending(true);
    try {
      const res = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/send-whatsapp-command",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: companyId,
            conversationId: selectedConvId,
            vendorPhone: vendorPhone,
          }),
        }
      );
      const result = await res.json();
      if (res.ok && result.success) {
        alert(
          "✅ Command sent! Check WhatsApp Web – images will be attached shortly."
        );
      } else {
        alert("❌ Failed: " + (result.error || "Unknown error"));
      }
    } catch (err) {
      alert("❌ Network error: " + err.message);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getMessageAlignment = (msg) => {
    if (msg.sender === "system") return "center";
    return msg.sender === "seller" ? "right" : "left";
  };

  return (
    <MDBContainer fluid className="vinted-inbox-container">
      <h2 className="mb-4">Vinted Inbox</h2>

      <div className="inbox-layout">
        {/* Conversation List */}
        <div className="conversation-list-panel">
          {loadingConversations ? (
            <p>Loading conversations...</p>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : conversations.length === 0 ? (
            <p>No conversations found.</p>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.conversation_id}
                className={`conversation-item ${
                  selectedConvId === conv.conversation_id ? "active" : ""
                }`}
                onClick={() => fetchMessages(conv.conversation_id)}
              >
                <img
                  src={conv.item_image_url || "https://via.placeholder.com/50"}
                  alt={conv.item_title}
                  className="conv-item-image"
                />
                <div className="conv-item-details">
                  <div className="conv-item-title">
                    <strong>{conv.buyer_name}</strong>
                    {conv.item_title && (
                      <span className="conv-item-product">
                        {" "}
                        – {conv.item_title}
                      </span>
                    )}
                  </div>
                  <div className="conv-item-preview">{conv.last_message}</div>
                  <div className="conv-item-time">
                    {formatTime(conv.last_updated)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Messages Panel */}
        <div className="messages-panel">
          {selectedConvId && (
            <div className="messages-toolbar">
              <button
                className="btn btn-sm btn-success"
                onClick={handleSendToVendor}
                disabled={sending}
              >
                {sending ? "Sending..." : "📤 Send to Vendor"}
              </button>
            </div>
          )}
          {selectedConvId ? (
            loadingMessages ? (
              <p>Loading messages...</p>
            ) : messages.length === 0 ? (
              <p>No messages yet.</p>
            ) : (
              <div className="messages-container">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message-bubble ${getMessageAlignment(msg)} ${
                      msg.message_type === "status" ? "status-message" : ""
                    } ${msg.message_type === "offer" ? "offer-message" : ""}`}
                  >
                    <div className="message-content">
                      <div className="message-text">{msg.message_body}</div>
                      <div className="message-time">
                        {formatTime(msg.sent_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <p>Select a conversation to view messages</p>
          )}
        </div>
      </div>
    </MDBContainer>
  );
};

export default VintedInboxPage;